import React, { useEffect, useRef } from 'react';
import './blog.css';
import kalilinuxinstalliation from '../../assets/kalilinuxinstalliation.png';
import nmaptutorial from '../../assets/nmaptutorial.png';

const blogData = [
  {
    id: 1,
    image: kalilinuxinstalliation,
    category: "Tutorial",
    title: "How To Install Kali Linux",
    date: "09 February, 2022",
    tag: "Kali Linux",
    readTime: "8 min read",
    description: "Learn how to install Kali Linux on your system with this comprehensive step-by-step guide for beginners.",
    author: "Professor01",
  },
  {
    id: 2,
    image: nmaptutorial,
    category: "Tutorial",
    title: "Nmap Full Course",
    date: "12 December, 2023",
    tag: "Nmap",
    readTime: "12 min read",
    description: "Master Nmap with this complete course covering network scanning, vulnerability detection, and security auditing.",
    author: "Professor01",
  },
  {
    id: 3,
    image: kalilinuxinstalliation,
    category: "Business",
    title: "3 Things to know about startup business",
    date: "05 February, 2023",
    tag: "Business",
    readTime: "6 min read",
    description: "Essential insights for aspiring entrepreneurs - learn the key factors that make startups successful in today's market.",
    author: "Professor01",
  },
];

const Blog = () => {
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    cardRefs.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'Tutorial':
        return '📚';
      case 'Business':
        return '💼';
      default:
        return '📝';
    }
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'Tutorial':
        return 'var(--blog-primary)';
      case 'Business':
        return 'var(--blog-secondary)';
      default:
        return 'var(--blog-accent)';
    }
  };

  return (
    <section className='blog section' id='blog' ref={sectionRef}>
      <div className='blog__bg-pattern'></div>
      
      <div className='blog__container container'>
        <div className='blog__header'>
          <span className='blog__badge'>Blog</span>
          <h2 className='section__title'>
            Latest <span className='gradient-text'>Posts</span>
          </h2>
          <div className='section__underline'></div>
          <p className='blog__tagline'>
            Insights, tutorials, and stories from my digital journey
          </p>
        </div>

        <div className='blog__grid'>
          {blogData.map((post, index) => (
            <div 
              className='blog__card' 
              key={post.id}
              ref={el => cardRefs.current[index] = el}
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              {/* Image Section */}
              <div className='blog__thumb'>
                <img src={post.image} alt={post.title} className='blog__img'/>
                
                {/* Category Badge */}
                <span className='blog__category' style={{ 
                  background: `linear-gradient(135deg, ${getCategoryColor(post.category)}, transparent)`,
                }}>
                  <span className='category-icon'>{getCategoryIcon(post.category)}</span>
                  {post.category}
                </span>

                {/* Read Time Badge */}
                <span className='blog__read-time'>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                  {post.readTime}
                </span>
              </div>

              {/* Content Section */}
              <div className='blog__details'>
                <div className='blog__meta'>
                  <span className='blog__date'>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    {post.date}
                  </span>
                  <span className='blog__dot'>•</span>
                  <span className='blog__tag'>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                      <line x1="7" y1="7" x2="7.01" y2="7"/>
                    </svg>
                    {post.tag}
                  </span>
                </div>

                <h3 className='blog__title'>{post.title}</h3>
                <p className='blog__description'>{post.description}</p>

                <div className='blog__footer'>
                  <div className='blog__author'>
                    <div className='blog__author-avatar'>
                      <span>DS</span>
                    </div>
                    <span className='blog__author-name'>{post.author}</span>
                  </div>
                  <a href='#' className='blog__read-more'>
                    <span>Read More</span>
                    <svg className='blog__arrow' width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                      <polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </a>
                </div>

                {/* Decorative Elements */}
                <div className='blog__deco deco-1'></div>
                <div className='blog__deco deco-2'></div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className='blog__view-all'>
          <a href='#' className='btn btn-outline'>
            <span>View All Posts</span>
            <svg className='btn-icon' width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Blog;